import React, { useState, useImperativeHandle, forwardRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch, useStore } from "react-redux";
import { addCreatives, addDimensions } from "../../../actions/app/creatives";
import AssetUploader from "./AssetUploader";
import { findNestedZips, isZipFile } from "../Shared/zipValidation";
import {
  getDurationOptions,
  snapSecondsToDuration
} from "../Shared/dimensionUtils";
import uuid from "uuid";

// Validator module for external IDs
export function isValidExternalId(filename, delimiter = "_") {
  if (!filename || typeof filename !== "string") return false;

  // Need at least 10 + 4 + 1 delimiter = 15 chars
  if (filename.length < 15) return false;

  // delimiter should be at index 14 (0-based)
  if (filename[14] !== delimiter) return false;

  const firstTen = filename.slice(0, 10);
  const lastFour = filename.slice(10, 14);

  // 10 alphanumeric characters followed by 4 digits
  const alphaNumRegex = /^[A-Za-z0-9]{10}$/;
  const digitsRegex = /^\d{4}$/;

  return alphaNumRegex.test(firstTen) && digitsRegex.test(lastFour);
}

export default forwardRef(
  (
    {
      isThumbnailEnabled,
      filetype,
      creativeTypes,
      selectedCreatives,
      setSelectedCreatives,
      placements,
      canChooseExisting = true,
      uploadLimit = 20,
      headerExtra
    },
    ref
  ) => {
    let dispatch = useDispatch();
    const store = useStore();
    const location = useLocation();
    let [snackbarState, setSnackbarState] = useState(false);
    let [snackbarMessage, setSnackbarMessage] = useState("");
    let creativeState = useSelector((state) => state.creatives);
    let assetsById = creativeState.byId;
    let assetIds = creativeState.allIds;

    // Snap an actual media duration (in seconds) to the nearest entry in the
    // configured Dimensions list (e.g. ":30", ":45"). Reads the store
    // directly so a metadata-load event that fires after a re-fetch sees the
    // current list. Returns null if the list configures no durations.
    //
    // Nearest, not largest-at-or-below: encoders routinely land a :30 spot at
    // 29.4s, and snapping down filed those under :15.
    const snapDurationToDimensionList = (seconds) =>
      snapSecondsToDuration(
        seconds,
        getDurationOptions(store.getState().typeGroups)
      );

    const handleDrop = async (acceptedFiles) => {
      // Pre-flight: reject any uploaded .zip that itself contains a nested
      // .zip file. GCM rejects HTML5 creatives whose asset list contains a
      // .zip with the cryptic error 9009 ("file type not supported"); this
      // surfaces the real cause before the user wastes time trafficking.
      let rejected = [];
      for (let file of acceptedFiles) {
        if (isZipFile(file)) {
          let nested = await findNestedZips(file);
          if (nested.length > 0) {
            rejected.push({ name: file.name, nested });
          }
        }
      }
      if (rejected.length > 0) {
        let lines = rejected.map(
          (r) => `• ${r.name} contains: ${r.nested.join(", ")}`
        );
        setSnackbarMessage(
          "The following H5 file(s) contain a nested .zip and cannot be trafficked. " +
            "Please re-package the bundle without the nested zip:\n" +
            lines.join("\n")
        );
        setSnackbarState(true);
        acceptedFiles = acceptedFiles.filter(
          (f) => !rejected.find((r) => r.name === f.name)
        );
        if (acceptedFiles.length === 0) return;
      }


      let newFiles = acceptedFiles.map((file) => {
        let id = uuid.v4(); //generate id

        // Only attempt image dimension extraction for image files
        if (file.type && file.type.startsWith('image/')) {
          let reader = new FileReader();

          reader.onload = function (e) {
            let img = new Image();
            img.src = e.target.result; //create Image with file as src

            img.onload = function () {
              let dimensions = this.width + " x " + this.height; //grab dimensions
              dispatch(addDimensions(id, dimensions)); //update dimensions property on creative
            };
          };

          reader.readAsDataURL(file); //read file
        }

        // For audio/video, derive duration as :NN seconds (the same slot the
        // dimensions field uses for media creatives). Failures are silent —
        // the creative just has no auto-derived dimension, like exotic image
        // codecs that don't decode.
        if (file.type && (file.type.startsWith('audio/') || file.type.startsWith('video/'))) {
          const isVideo = file.type.startsWith('video/');
          const media = isVideo ? document.createElement('video') : new Audio();
          media.preload = 'metadata';
          const objectUrl = URL.createObjectURL(file);

          const cleanup = () => {
            URL.revokeObjectURL(objectUrl);
            media.removeEventListener('loadedmetadata', onLoaded);
            media.removeEventListener('error', cleanup);
          };

          const onLoaded = () => {
            const exactSeconds = media.duration;
            const seconds = Math.round(exactSeconds);
            if (Number.isFinite(seconds) && seconds > 0) {
              // Snap to the configured Dimensions list when possible, fall
              // back to the raw `:NN` value otherwise. Either way we want
              // creative.dimensions populated so the bulk-save preservation
              // logic (CreativeNameManager.handleSave) doesn't leak a WxH
              // dropdown pick onto a media creative.
              const snapped =
                snapDurationToDimensionList(exactSeconds) || ":" + seconds;
              dispatch(addDimensions(id, snapped));
            }
            cleanup();
          };

          media.addEventListener('loadedmetadata', onLoaded);
          media.addEventListener('error', cleanup);
          media.src = objectUrl;
        }

        let externalId = "";

        if (isValidExternalId(file.name)) externalId = file.name.slice(0, 14);

        return {
          id,
          externalId,
          file: file,
          preview: URL.createObjectURL(file),
          weight: 0,
          createdInPreloader: location.pathname.includes("preloader")
            ? true
            : false,
          taxonomy: { externalId }
        };
      });

      if (newFiles.length + assetIds.length <= uploadLimit) {
        dispatch(addCreatives(newFiles));
      } else {
        setSnackbarMessage(""); // fall back to default upload-limit text
        setSnackbarState(true);
      }
    };

    useImperativeHandle(ref, () => ({
      validate() {
        if (assetIds.length === 0) {
          return {
            hasErrors: true,
            message: "You need to add at least one asset."
          };
        }

        return {
          hasErrors: false,
          message: ""
        };
      }
    }));

    return (
      <AssetUploader
        assetsById={assetsById}
        assetIds={assetIds}
        weightDistribution={creativeState.weightDistribution}
        rotationType={creativeState.rotationType}
        handleDrop={handleDrop}
        snackbarOpen={snackbarState}
        setSnackbarState={setSnackbarState}
        snackbarMessage={snackbarMessage}
        isThumbnailEnabled={isThumbnailEnabled}
        filetype={filetype}
        creativeTypes={creativeTypes}
        selectedCreatives={selectedCreatives}
        setSelectedCreatives={setSelectedCreatives}
        placements={placements}
        canChooseExisting={canChooseExisting}
        uploadLimit={uploadLimit}
        headerExtra={headerExtra}
      />
    );
  }
);
